import { Space, Typography } from 'antd'
import { CIMSTags } from 'components/CIMSTags'
import { usePermissionsContext } from '../../../PermissionsContext'
import {
  CLOSED_STATUS_ID,
  DRAFT_STATUS_ID,
  normalizePermissionConditions,
  permissionConditionName,
} from '../../../utils'
import { getPermissionActionMetadata } from '../../../metadata'
import {
  WorkflowPermissionField,
  WorkflowPermissionRecord,
  WorkflowPermissionStatus,
} from '../../../types'

type SelectableRecord = WorkflowPermissionField | WorkflowPermissionStatus

type ConditionTagsProps<T extends SelectableRecord> = {
  ids: string[]
  label: string
  records: Map<string, T>
  type: 'field' | 'status'
}

export type PermissionConditionsProps = {
  record: WorkflowPermissionRecord
}

function PermissionConditions({ record }: PermissionConditionsProps) {
  const {
    metadata: { fieldMap, statusMap },
  } = usePermissionsContext()
  const metadata = getPermissionActionMetadata(record.action)
  if (!metadata.conditionKeys.length) return null
  const conditions = normalizePermissionConditions(record.conditions)
  return (
    <Space direction="vertical" size={15}>
      {metadata.conditionKeys.includes('fieldIds') && (
        <ConditionTags
          label="Fields"
          ids={conditions.fieldIds}
          records={fieldMap}
          type="field"
        />
      )}
      {metadata.conditionKeys.includes('approvalStatusId') &&
        conditions.approvalStatusId && (
          <ConditionTags
            label="Approval Status"
            ids={[conditions.approvalStatusId]}
            records={statusMap}
            type="status"
          />
        )}
      {metadata.conditionKeys.includes('statusIds') && (
        <ConditionTags
          label="Statuses"
          ids={conditions.statusIds}
          records={statusMap}
          type="status"
        />
      )}
    </Space>
  )
}

function ConditionTags<T extends SelectableRecord>({
  ids,
  label,
  records,
  type,
}: ConditionTagsProps<T>) {
  if (!ids.length) return null
  return (
    <CIMSTags
      header={{
        content: <Typography.Text strong>{label}</Typography.Text>,
      }}
      direction={{ header: 'vertical', tags: 'horizontal' }}
      tags={ids.map((id) => {
        if (
          type === 'status' &&
          (id === CLOSED_STATUS_ID || id === DRAFT_STATUS_ID)
        )
          return {
            text: permissionConditionName(id, records, type),
            tooltip: 'System status',
          }

        const record = records.get(id)
        return {
          text: permissionConditionName(id, records, type),
          tooltip: record?.tooltip ?? (!record ? `Unknown ${type}` : undefined),
          strikethrough: record?.deleted ?? !record,
        }
      })}
    />
  )
}

export default PermissionConditions
